import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowUpRight,
  Menu,
  Check,
  X,
  Info,
  Minus,
  Plus,
  CreditCard,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useChatLayout } from '../../components/layout/ChatLayout';
import WorkspacePill from '../../components/layout/WorkspacePill';
import { usePermissions } from '../../contexts/AuthContext';
import {
  billingApi,
  handleApiError,
  type BillingTier,
  type PaidTier,
} from '../../services/api';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

// --- Tier display metadata (mirrors backend TIER_CONFIG; display-only) --------

interface TierMeta {
  name: string;
  priceUsd: number;
  sources: number;
  seats: number;
  addon: { source: number; seat: number } | null;
  lede: string;
  features: string[];
  /** Announced but not yet purchasable — rendered non-selectable. */
  comingSoon?: boolean;
}

// Scale is withdrawn from sale (PRICING_V3_SPEC.md §3.1) — absent here so it
// never appears in the picker. Its TIER_META entry stays so a workspace already
// on scale still renders its current plan.
const TIER_ORDER: BillingTier[] = ['free', 'starter', 'business'];

const TIER_META: Record<BillingTier, TierMeta> = {
  free: {
    name: 'Free', priceUsd: 0, sources: 2, seats: 1, addon: null,
    lede: 'Get started and keep your data connected.',
    features: ['25 AI queries a day', 'Full history', 'Business glossary & wiki'],
  },
  starter: {
    name: 'Starter', priceUsd: 9, sources: 2, seats: 1, addon: null,
    lede: 'Unlimited questions for a solo operator.',
    features: ['Unlimited AI queries', 'Everything in Free', '30-day free trial'],
  },
  business: {
    name: 'Business', priceUsd: 19, sources: 10, seats: 1,
    addon: { source: 5, seat: 6 },
    lede: 'Cross-source metrics for a team.',
    features: ['Everything in Starter', 'Composite metrics', 'Add sources $5 · seats $6'],
  },
  scale: {
    name: 'Scale', priceUsd: 79, sources: 6, seats: 1,
    addon: { source: 12, seat: 8 },
    lede: 'The full platform — Pulse, AI chat & dashboards.',
    features: ['Everything in Business', 'Pulse monitoring', 'AI chat', 'Dashboards', 'Add sources $12 · seats $8'],
    comingSoon: true,
  },
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// --- Page ---------------------------------------------------------------------

const BillingPage: React.FC = () => {
  const { canManageBilling, isOwner } = usePermissions();
  const { isMobile, toggleSidebar } = useChatLayout();
  const queryClient = useQueryClient();

  const entQuery = useQuery({
    queryKey: ['billing', 'entitlements'],
    queryFn: billingApi.getEntitlements,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: canManageBilling(),
  });
  const subQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: billingApi.getSubscription,
    staleTime: 60_000,
    enabled: canManageBilling(),
  });

  const ent = entQuery.data;

  // Staged add-on quantities (reset to server values whenever entitlements load).
  const [stagedSources, setStagedSources] = useState(0);
  const [stagedSeats, setStagedSeats] = useState(0);
  useEffect(() => {
    if (ent) {
      setStagedSources(ent.sources.extra);
      setStagedSeats(ent.seats.extra);
    }
  }, [ent]);

  const [addonConfirmOpen, setAddonConfirmOpen] = useState(false);
  const [planChangeTarget, setPlanChangeTarget] = useState<BillingTier | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['billing'] });
  };

  const addonMutation = useMutation({
    mutationFn: async (changes: { kind: 'sources' | 'seats'; delta: number }[]) => {
      for (const c of changes) await billingApi.adjustAddon(c.kind, c.delta);
    },
    onSuccess: () => { toast.success('Add-ons updated'); invalidate(); setAddonConfirmOpen(false); },
    onError: (e) => toast.error(handleApiError(e) || 'Could not update add-ons'),
  });

  // Plan changes run through our own API. Stripe's hosted Portal cannot do them:
  // its subscription_update flow rejects multi-item subscriptions, and ours always
  // has several (base + pooled source/seat add-ons). The Portal is for payment
  // methods, invoices and cancellation only.
  //
  // The one exception is starting a subscription that doesn't exist yet (Free or
  // trialing) — that has to be Checkout, so we can collect a payment method.
  const onPaidSub = !!ent && ent.tier !== 'free' && ent.subscription_status !== 'trialing';
  const planMutation = useMutation({
    mutationFn: async (tier: BillingTier) => {
      if (!onPaidSub) {
        if (tier === 'free') return null; // nothing to start
        const { url } = await billingApi.createCheckoutSession(tier as PaidTier);
        window.location.href = url;
        return null;
      }
      return billingApi.changePlan(tier);
    },
    onSuccess: (result) => {
      if (!result) return; // redirecting to Checkout
      toast.success(
        result.effectiveAt === 'now'
          ? 'Plan upgraded — prorated for the rest of this period.'
          : `Plan change scheduled for ${formatDate(result.effectiveDate)}.`,
      );
      invalidate();
      setPlanChangeTarget(null);
    },
    onError: (e) => toast.error(handleApiError(e) || 'Could not change plan'),
  });

  const cancelPlanChangeMutation = useMutation({
    mutationFn: billingApi.cancelPlanChange,
    onSuccess: () => { toast.success('Staying on your current plan.'); invalidate(); },
    onError: (e) => toast.error(handleApiError(e) || 'Could not cancel the scheduled change'),
  });

  const portalMutation = useMutation({
    mutationFn: billingApi.createPortalSession,
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (e) => toast.error(handleApiError(e) || 'Could not open billing portal'),
  });

  if (!canManageBilling()) {
    return (
      <PageShell isMobile={isMobile} toggleSidebar={toggleSidebar}>
        <div className="rounded-xl bg-muted border border-border p-8 text-center">
          <CreditCard className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-foreground">Admins only</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Billing is managed by workspace owners and admins.
          </p>
        </div>
      </PageShell>
    );
  }

  if (entQuery.isLoading || !ent) {
    return (
      <PageShell isMobile={isMobile} toggleSidebar={toggleSidebar}>
        <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
      </PageShell>
    );
  }

  const tier = ent.tier;
  const meta = TIER_META[tier];
  const sub = subQuery.data;
  const status = ent.subscription_status;
  // A queued cancellation is just a downgrade to Free — present them as one thing.
  const pendingTier: BillingTier | null = sub?.cancelAtPeriodEnd ? 'free' : sub?.pendingTier ?? null;
  const sourceUnit = ent.sources.unitPriceUsd ?? 0;
  const seatUnit = ent.seats.unitPriceUsd ?? 0;
  const estTotal = meta.priceUsd + stagedSources * sourceUnit + stagedSeats * seatUnit;

  const srcDelta = stagedSources - ent.sources.extra;
  const seatDelta = stagedSeats - ent.seats.extra;
  const addonDirty = srcDelta !== 0 || seatDelta !== 0;

  const statusPill = () => {
    if (status === 'trialing') {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-warn/15 text-warn">
        Trial ends {formatDate(sub?.trialEndsAt)}
      </span>;
    }
    if (status === 'past_due') {
      return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-destructive/15 text-destructive">Past due</span>;
    }
    if (status === 'canceled') {
      return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground">Canceled</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-success/15 text-success">Active</span>;
  };

  const confirmAddonChanges = () => {
    const changes: { kind: 'sources' | 'seats'; delta: number }[] = [];
    if (srcDelta !== 0) changes.push({ kind: 'sources', delta: srcDelta });
    if (seatDelta !== 0) changes.push({ kind: 'seats', delta: seatDelta });
    addonMutation.mutate(changes);
  };

  return (
    <PageShell isMobile={isMobile} toggleSidebar={toggleSidebar}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/settings" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Plans &amp; billing</h1>
            <p className="text-sm text-muted-foreground">
              Billing for the <span className="text-foreground font-medium">{sub?.orgName ?? 'workspace'}</span> workspace.
              Sources and seats are pooled across every organization.
            </p>
          </div>
        </div>
        <WorkspacePill />
      </div>

      {/* CURRENT PLAN */}
      <div className="rounded-xl bg-card border border-border p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-accent text-primary grid place-items-center shrink-0">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-foreground">{meta.name}</span>
              {statusPill()}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 tabular-nums">
              ${meta.priceUsd}/mo base
              {status === 'active' && sub?.currentPeriodEnd && <> · renews {formatDate(sub.currentPeriodEnd)}</>}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-1">
          <Button variant="outline" onClick={() => portalMutation.mutate()} isLoading={portalMutation.isPending}
            rightIcon={<ArrowUpRight className="w-4 h-4" />}>
            Payment methods, invoices &amp; cancellation
          </Button>
          <span className="text-xs text-muted-foreground">Opens Stripe, our payment provider</span>
        </div>
      </div>

      {/* SCHEDULED PLAN CHANGE */}
      {pendingTier && (
        <div className="rounded-xl bg-info/10 border border-info/20 p-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-info shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              Scheduled: you move to <strong>{TIER_META[pendingTier].name}</strong>
              {sub?.currentPeriodEnd && <> on <strong>{formatDate(sub.currentPeriodEnd)}</strong></>}.
              You keep {meta.name} and everything on it until then — nothing is charged or refunded today.
            </p>
          </div>
          <Button variant="outline" onClick={() => cancelPlanChangeMutation.mutate()}
            isLoading={cancelPlanChangeMutation.isPending}>
            Stay on {meta.name}
          </Button>
        </div>
      )}

      {/* USAGE + ADD-ONS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl bg-card border border-border p-5">
          <Meter label="Data sources" used={ent.sources.used} limit={ent.sources.limit} />
          <div className="mt-3.5">
            <Meter label="Seats" used={ent.seats.used} limit={ent.seats.limit} />
          </div>
          {ent.mcpQuota && (
            <div className="mt-3.5">
              <Meter
                label="AI queries today"
                used={ent.mcpQuota.used}
                limit={ent.mcpQuota.limit}
                suffix="used today"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Questions your AI asks over MCP. Resets daily; every paid plan is unlimited.
              </p>
            </div>
          )}

          {ent.sources.addonAvailable ? (
            <>
              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <Stepper label="Extra sources" unit={sourceUnit} value={stagedSources} onChange={setStagedSources} />
                <Stepper label="Extra seats" unit={seatUnit} value={stagedSeats} onChange={setStagedSeats} />
              </div>
              {(ent.sources.pendingExtra != null || ent.seats.pendingExtra != null) && (
                <div className="mt-3 rounded-lg bg-info/10 border border-info/20 p-2.5 text-xs text-muted-foreground space-y-1.5">
                  {ent.sources.pendingExtra != null && (
                    <PendingDowngradeRow
                      label="Extra sources" now={ent.sources.extra} next={ent.sources.pendingExtra}
                      onCancel={() => addonMutation.mutate([{ kind: 'sources', delta: 0 }])}
                      disabled={addonMutation.isPending}
                    />
                  )}
                  {ent.seats.pendingExtra != null && (
                    <PendingDowngradeRow
                      label="Extra seats" now={ent.seats.extra} next={ent.seats.pendingExtra}
                      onCancel={() => addonMutation.mutate([{ kind: 'seats', delta: 0 }])}
                      disabled={addonMutation.isPending}
                    />
                  )}
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-dashed border-border flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estimated monthly total</span>
                <span className="text-lg font-bold text-foreground tabular-nums">${estTotal}<span className="text-xs font-medium text-muted-foreground">/mo</span></span>
              </div>
              <Button className="w-full mt-3" disabled={!addonDirty}
                onClick={() => setAddonConfirmOpen(true)}>
                {addonDirty ? 'Review changes' : 'No changes to save'}
              </Button>
            </>
          ) : (
            <div className="mt-4 pt-4 border-t border-border flex items-start gap-2 text-sm text-muted-foreground">
              <Info className="w-4 h-4 text-info shrink-0 mt-0.5" />
              <span>Your plan doesn't include add-on sources or seats. Upgrade to <strong>Business</strong> to add more.</span>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-card border border-border p-5 flex flex-col">
          <div className="text-sm font-medium text-foreground mb-3">What's included on {meta.name}</div>
          <ul className="space-y-2.5">
            <li className="flex items-start gap-2 text-sm text-foreground">
              <Check className="w-4 h-4 text-success shrink-0 mt-0.5" />
              <span><strong className="tabular-nums">{meta.sources}</strong> data sources pooled across the workspace</span>
            </li>
            {meta.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-foreground">
                <Check className="w-4 h-4 text-success shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* COMPARE PLANS */}
      <div>
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg font-semibold text-foreground">Compare plans</h2>
          <span className="text-xs text-muted-foreground">Upgrades apply now (prorated) · downgrades take effect at renewal.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TIER_ORDER.map((t) => (
            <PlanCard key={t} planTier={t} currentTier={tier} onSelect={() => setPlanChangeTarget(t)} />
          ))}
        </div>
      </div>

      {/* DANGER ZONE */}
      <div>
        <h2 className="text-lg font-semibold text-destructive mb-3">Danger zone</h2>
        <Link
          to="/settings/account-closure"
          className="flex items-center justify-between gap-4 rounded-xl border border-destructive/30 bg-destructive/[0.04] p-5 hover:bg-destructive/10 transition-colors"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-foreground">
                {isOwner ? 'Close this workspace' : 'Delete my account'}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                {isOwner
                  ? 'End access for everyone and permanently delete all workspace data.'
                  : 'Remove yourself from this workspace and permanently delete your account.'}
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
        </Link>
      </div>

      {/* ADD-ON CONFIRM MODAL */}
      <Modal isOpen={addonConfirmOpen} onClose={() => setAddonConfirmOpen(false)} size="sm">
        <ModalHeader onClose={() => setAddonConfirmOpen(false)}>Confirm add-on changes</ModalHeader>
        <ModalBody>
          <div className="space-y-2 text-sm text-foreground">
            {srcDelta !== 0 && (
              <DeltaLine label="data source" delta={srcDelta} unit={sourceUnit} />
            )}
            {seatDelta !== 0 && (
              <DeltaLine label="seat" delta={seatDelta} unit={seatUnit} />
            )}
          </div>
          <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Monthly total going forward</span>
            <span className="text-base font-bold text-foreground tabular-nums">${estTotal}/mo</span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {srcDelta < 0 || seatDelta < 0
              ? 'Additions are prorated and charged now. Reductions take effect at your next renewal — you keep the capacity until then, with no mid-cycle refund.'
              : 'Changes are prorated for the rest of this billing period and applied to your Stripe subscription.'}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setAddonConfirmOpen(false)} disabled={addonMutation.isPending}>Cancel</Button>
          <Button onClick={confirmAddonChanges} isLoading={addonMutation.isPending}>Confirm &amp; update billing</Button>
        </ModalFooter>
      </Modal>

      {/* PLAN CHANGE CONFIRM MODAL */}
      <Modal isOpen={planChangeTarget !== null} onClose={() => setPlanChangeTarget(null)} size="sm">
        {planChangeTarget && (() => {
          const isUpgrade = tierRank(planChangeTarget) > tierRank(tier);
          const target = TIER_META[planChangeTarget];
          return (
            <>
              <ModalHeader onClose={() => setPlanChangeTarget(null)}>
                {isUpgrade ? 'Upgrade' : 'Change'} to {target.name}
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-foreground">
                  {target.name} is <strong className="tabular-nums">${target.priceUsd}/mo</strong>, billed to this workspace.
                </p>
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-info/10 border border-info/20 p-3 text-sm text-muted-foreground">
                  <Info className="w-4 h-4 text-info shrink-0 mt-0.5" />
                  {!onPaidSub ? (
                    <span>You'll enter payment details on Stripe's secure checkout to start your subscription.</span>
                  ) : isUpgrade ? (
                    <span>
                      Applies immediately. You'll be charged a prorated amount for the rest of this
                      billing period, then <span className="tabular-nums">${target.priceUsd}/mo</span> from{' '}
                      {formatDate(sub?.currentPeriodEnd)}.
                    </span>
                  ) : (
                    <span>
                      Takes effect on <strong>{formatDate(sub?.currentPeriodEnd)}</strong>. You keep {meta.name} and
                      everything on it until then — nothing is charged or refunded today, and you can undo this at
                      any point before that date.
                    </span>
                  )}
                </div>
                {!isUpgrade && onPaidSub && !TIER_META[planChangeTarget].addon && meta.addon && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {target.name} has no add-ons, so your extra sources and seats end on that date too. Anything over
                    the new limit is deactivated (never deleted) — newest first.
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="outline" onClick={() => setPlanChangeTarget(null)} disabled={planMutation.isPending}>Cancel</Button>
                <Button onClick={() => planMutation.mutate(planChangeTarget)} isLoading={planMutation.isPending}>
                  {!onPaidSub ? `Continue to ${target.name}` : isUpgrade ? `Upgrade to ${target.name}` : 'Schedule change'}
                </Button>
              </ModalFooter>
            </>
          );
        })()}
      </Modal>
    </PageShell>
  );
};

// --- Sub-components -----------------------------------------------------------

const PageShell: React.FC<{ isMobile: boolean; toggleSidebar: () => void; children: React.ReactNode }> = ({
  isMobile, toggleSidebar, children,
}) => (
  <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
    {isMobile && (
      <button onClick={toggleSidebar}
        className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" aria-label="Open sidebar">
        <Menu className="w-5 h-5" />
      </button>
    )}
    <div className="w-full max-w-5xl mx-auto space-y-6">{children}</div>
  </div>
);

const Meter: React.FC<{ label: string; used: number; limit: number; suffix?: string }> = ({
  label, used, limit, suffix = 'included',
}) => {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{used} of {limit} {suffix}</span>
      </div>
      <div className="h-2 rounded-full bg-muted mt-2 overflow-hidden">
        <div className={clsx('h-full rounded-full', pct >= 90 ? 'bg-warn' : 'bg-primary')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Stepper: React.FC<{ label: string; unit: number; value: number; onChange: (v: number) => void }> = ({
  label, unit, value, onChange,
}) => (
  <div className="flex items-center justify-between gap-3">
    <div className="text-sm">
      <span className="font-medium text-foreground">{label}</span>
      <span className="text-muted-foreground tabular-nums"> · ${unit}/mo each</span>
    </div>
    <div className="inline-flex items-center rounded-lg border border-input overflow-hidden">
      <button type="button" aria-label={`Remove ${label}`} onClick={() => onChange(Math.max(0, value - 1))}
        className="w-8 h-8 grid place-items-center text-foreground hover:bg-muted disabled:opacity-40" disabled={value <= 0}>
        <Minus className="w-4 h-4" />
      </button>
      <span className="w-9 text-center text-sm tabular-nums border-x border-input leading-8">{value}</span>
      <button type="button" aria-label={`Add ${label}`} onClick={() => onChange(value + 1)}
        className="w-8 h-8 grid place-items-center text-foreground hover:bg-muted">
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const DeltaLine: React.FC<{ label: string; delta: number; unit: number }> = ({ label, delta, unit }) => {
  const n = Math.abs(delta);
  const add = delta > 0;
  return (
    <div className="flex items-center justify-between">
      <span>{add ? 'Add' : 'Remove'} {n} {label}{n !== 1 ? 's' : ''}</span>
      <span className={clsx('font-medium tabular-nums', add ? 'text-foreground' : 'text-success')}>
        {add ? '+' : '−'}${n * unit}/mo
      </span>
    </div>
  );
};

const PendingDowngradeRow: React.FC<{
  label: string; now: number; next: number; onCancel: () => void; disabled: boolean;
}> = ({ label, now, next, onCancel, disabled }) => (
  <div className="flex items-start gap-2">
    <Info className="w-3.5 h-3.5 text-info shrink-0 mt-0.5" />
    <span className="flex-1">
      {label} drop to <strong className="tabular-nums">{next}</strong> at renewal — you keep{' '}
      <strong className="tabular-nums">{now}</strong> until then.
    </span>
    <button className="text-primary font-medium hover:underline shrink-0 disabled:opacity-50"
      onClick={onCancel} disabled={disabled}>
      Cancel
    </button>
  </div>
);

function tierRank(t: BillingTier): number { return TIER_ORDER.indexOf(t); }

const PlanCard: React.FC<{ planTier: BillingTier; currentTier: BillingTier; onSelect: () => void }> = ({
  planTier, currentTier, onSelect,
}) => {
  const m = TIER_META[planTier];
  const isCurrent = planTier === currentTier;
  const isUpgrade = tierRank(planTier) > tierRank(currentTier);
  const popular = planTier === 'business';
  const comingSoon = !!m.comingSoon;

  return (
    <div className={clsx(
      'relative rounded-xl bg-card border p-4 flex flex-col gap-3',
      isCurrent ? 'border-primary ring-1 ring-primary' : 'border-border',
      comingSoon && 'opacity-90',
    )}>
      {popular && !isCurrent && (
        <span className="absolute -top-2.5 left-4 text-[10px] font-bold uppercase tracking-wide bg-primary text-primary-foreground px-2 py-0.5 rounded">
          Most popular
        </span>
      )}
      {comingSoon && (
        <span className="absolute -top-2.5 left-4 text-[10px] font-bold uppercase tracking-wide bg-foreground text-background px-2 py-0.5 rounded">
          Coming soon
        </span>
      )}
      <div className="text-sm font-semibold text-foreground">{m.name}</div>
      <div className="text-2xl font-bold text-foreground tabular-nums">
        ${m.priceUsd}<span className="text-xs font-medium text-muted-foreground">/mo</span>
      </div>
      <p className="text-xs text-muted-foreground min-h-[32px] leading-snug">{m.lede}</p>
      <div className="flex flex-wrap gap-1.5">
        <Chip>{m.sources} sources</Chip>
        <Chip>{m.seats} seat</Chip>
        {m.addon && <Chip>+add-ons</Chip>}
      </div>
      <ul className="space-y-1.5 flex-1">
        {m.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-foreground">
            <Check className="w-3.5 h-3.5 text-success shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
        {!m.addon && (
          <li className="flex items-start gap-2 text-xs text-muted-foreground">
            <X className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>No add-on sources / seats</span>
          </li>
        )}
      </ul>
      <div className="mt-auto">
        {isCurrent ? (
          <Button variant="primary" className="w-full" disabled>Current plan</Button>
        ) : comingSoon ? (
          <Button variant="outline" className="w-full" disabled>Coming soon</Button>
        ) : (
          <Button variant={isUpgrade ? 'primary' : 'outline'} className="w-full" onClick={onSelect}>
            {isUpgrade ? 'Upgrade' : 'Downgrade'}
          </Button>
        )}
      </div>
    </div>
  );
};

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] bg-muted rounded px-2 py-0.5 text-foreground tabular-nums">{children}</span>
);

export default BillingPage;
